import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Linkedin, Twitter, Phone, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { NAV_LINKS, CONTACT_INFO } from '../mock/data';
import Wordmark from './Wordmark';

/**
 * BrandMark — displays the exact "iT" PNG supplied by the client, unmodified,
 * inside a subtle cyan-tinted container that syncs with the site's palette.
 */
const BrandMark = ({ size = 44 }) => (
  <div
    className="relative shrink-0 group-hover:scale-105 transition-transform duration-300"
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    <div
      className="relative w-full h-full rounded-xl overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #0B1120 0%, #020617 100%)',
        border: '1px solid rgba(34, 211, 238, 0.35)',
        boxShadow: 'inset 0 0 20px rgba(34, 211, 238, 0.12)',
      }}
    >
      <img
        src={`${process.env.PUBLIC_URL || ''}/brand/it-mark.png`}
        alt="The Tech Reach"
        className="absolute inset-0 w-full h-full object-contain"
        style={{ padding: '8%' }}
      />
    </div>
    <div
      className="absolute -inset-2 rounded-2xl bg-cyan-400/25 blur-xl -z-10 opacity-70 group-hover:opacity-100 transition-opacity"
      style={{ pointerEvents: 'none' }}
    />
  </div>
);

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 group" aria-label="The Tech Reach">
    <BrandMark size={44} />
    <div className="hidden sm:block">
      <Wordmark size="nav" showTagline />
    </div>
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#05070E]/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
      <div className="container-x flex items-center justify-between h-20">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map(l => (
            <Link key={l.path} to={l.path} className={`nav-link text-sm font-medium ${location.pathname === l.path ? 'active' : ''}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" className="btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm">
            Start a Conversation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-[#05070E]/95 backdrop-blur-xl border-t border-white/5">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV_LINKS.map(l => (
              <Link key={l.path} to={l.path} className={`nav-link text-base ${location.pathname === l.path ? 'active' : ''}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full mt-2">
              Start a Conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="relative border-t border-white/5 bg-[#05070E]">
    <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
    <div className="container-x relative py-16">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
        <div className="md:col-span-1">
          <Link to="/" className="flex items-center gap-3 group" aria-label="The Tech Reach">
            <BrandMark size={40} />
            <Wordmark size="sm" showTagline />
          </Link>
          <p className="text-sm text-white/60 mt-4 leading-relaxed">
            Your trusted partner in IT recruitment, staffing, and SaaS solutions — connecting exceptional talent with ambitious companies.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a href={CONTACT_INFO.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-cyan-400 hover:text-cyan-400 transition-colors" aria-label="LinkedIn">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href={CONTACT_INFO.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-cyan-400 hover:text-cyan-400 transition-colors" aria-label="Twitter">
              <Twitter className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-white mb-4">Services</h4>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/talent" className="hover:text-cyan-400 transition-colors">IT Talent Acquisition</Link></li>
            <li><Link to="/services" className="hover:text-cyan-400 transition-colors">Technology Consulting</Link></li>
            <li><Link to="/services" className="hover:text-cyan-400 transition-colors">SaaS Implementation</Link></li>
            <li><Link to="/services" className="hover:text-cyan-400 transition-colors">Workforce Solutions</Link></li>
            <li><Link to="/services" className="hover:text-cyan-400 transition-colors">BPO & KPO</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Us</Link></li>
            <li><Link to="/industries" className="hover:text-cyan-400 transition-colors">Industries</Link></li>
            <li><Link to="/careers" className="hover:text-cyan-400 transition-colors">Careers</Link></li>
            <li><Link to="/contact" className="hover:text-cyan-400 transition-colors">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-white mb-4">Contact</h4>
          <ul className="space-y-3 text-sm text-white/60">
            <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 mt-0.5 text-cyan-400 shrink-0" />{CONTACT_INFO.address}</li>
            <li className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-cyan-400" /><a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-white">{CONTACT_INFO.phone}</a></li>
            <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-cyan-400" /><a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white">{CONTACT_INFO.email}</a></li>
          </ul>
        </div>
      </div>
      <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-xs text-white/40">© {new Date().getFullYear()} The Tech Reach. All rights reserved.</p>
        <div className="flex gap-6 text-xs text-white/40">
          <span className="hover:text-white cursor-pointer transition-colors">Privacy Policy</span>
          <span className="hover:text-white cursor-pointer transition-colors">Terms of Service</span>
          <span className="hover:text-white cursor-pointer transition-colors">Cookies</span>
        </div>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [location.pathname]);
  return (
    <>
      <Navbar />
      <main className="pt-20">{children}</main>
      <Footer />
    </>
  );
};

export default Layout;
